/**
 * "O que fazer agora" da lista: as acoes que mais valem dinheiro, com o
 * botao que ja leva ao lugar certo (recorte da lista ou aba da empresa).
 *
 * Mostra quatro de cara e o resto atras de "ver mais": com centenas de
 * empresas a lista de insights nao pode empurrar a tabela para fora da tela.
 * Tom de cada item vem com icone e rotulo escrito (sr-only), nunca so cor.
 */

import { useState } from 'react';
import { Button, Icon, SectionCard } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import type { AcaoInsight, InsightLista, TomInsight } from '../insights';

const VISIVEIS = 4;

const TOM: Readonly<Record<TomInsight, { readonly caixa: string; readonly icone: string; readonly rotulo: string }>> = {
  critical: { caixa: 'border-status-critical/40 bg-status-critical/5', icone: 'bg-status-critical/15 text-status-critical', rotulo: 'Crítico' },
  warning: { caixa: 'border-status-warning/50 bg-status-warning/5', icone: 'bg-status-warning/15 text-status-warning', rotulo: 'Atenção' },
  info: { caixa: 'border-info/30 bg-info/5', icone: 'bg-info/15 text-info', rotulo: 'Oportunidade' },
  good: { caixa: 'border-status-good/40 bg-status-good/5', icone: 'bg-status-good/15 text-status-good', rotulo: 'Boa notícia' },
};

export function OQueFazerAgora({
  insights,
  onAcao,
  className = '',
}: {
  readonly insights: readonly InsightLista[];
  readonly onAcao: (acao: AcaoInsight) => void;
  readonly className?: string;
}): JSX.Element {
  const [todos, setTodos] = useState(false);
  const visiveis = todos ? insights : insights.slice(0, VISIVEIS);
  const escondidos = insights.length - visiveis.length;

  return (
    <SectionCard
      id="o-que-fazer-agora"
      title="O que fazer agora"
      icon="raio"
      description="Derivado da saúde e do retorno de todas as empresas, do que perde lead agora para o que é oportunidade."
      className={className}
    >
      {insights.length === 0 ? (
        <p className="text-sm text-ink-muted">Nenhuma empresa na tela para diagnosticar. Cadastre a primeira ou limpe os filtros.</p>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {visiveis.map((insight) => {
            const tom = TOM[insight.tom];
            return (
              <li key={insight.id} className={`flex flex-col gap-2 rounded-xl border px-4 py-3 ${tom.caixa}`}>
                <div className="flex items-start gap-3">
                  <span aria-hidden="true" className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${tom.icone}`}>
                    <Icon name={insight.icon} size="sm" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">
                      <span className="sr-only">{tom.rotulo}: </span>
                      {insight.titulo}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-muted">{insight.detalhe}</p>
                  </div>
                </div>
                <div className="mt-auto pl-11">
                  <Button variant="secondary" size="sm" iconRight="seta-dir" onClick={() => onAcao(insight.acao)}>
                    {insight.acao.rotulo}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {escondidos > 0 || todos ? (
        <div className="mt-3">
          <Button variant="ghost" size="sm" icon={todos ? 'chevron' : 'mais'} onClick={() => setTodos((v) => !v)} aria-expanded={todos}>
            {todos ? 'Mostrar menos' : `Ver mais ${formatCount(escondidos)}`}
          </Button>
        </div>
      ) : null}
    </SectionCard>
  );
}
